import React, { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Icon, Popup } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfitiveScrollDropown from '~/components/InfitiveScrollDropown'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import { El } from '~/components/Layout/style'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ShowMoreDialog from './ShowMoreDialog'
import { handleErrors } from '~/utils/helpers'
import { GeneralStoreType, IndividualFiltersService } from '~/api/services/individualFilters'
import notify from '~/components/Notify'
import { useMutation } from 'react-query'
import { FarmersService } from '~/api/services/farmers/index'
import { SelectedContent, SelectedWrapper } from '../style'

const MIN_FARMERS_TO_DISPLAY = 8

const CreateEditGenFilter: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)

  const { control, handleSubmit, setValue, reset } = useForm({
    mode: 'onSubmit'
  })

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'farmers',
    keyName: 'key'
  })

  const {
    data: farmers,
    isLoading: isLoadingFarmers,
    mutate: getFarmers
  } = useMutation(
    'individual-filter-farmers',
    FarmersService.getFarmers,
    {
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  useEffect(() => {
    if (id) {
      fetchData()
    } else {
      getFarmers({ isMain: null, selected: [], page: 0, search: '' })
    }
  }, [id])

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await IndividualFiltersService.filtersGeneralEdit(id)
      getFarmers({
        isMain: null,
        selected: response.data?.farmers?.map(farmer => farmer.id),
        page: 0,
        search: ''
      })
      reset({
        ...response.data
      })
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function onSubmit(data: GeneralStoreType) {
    if (data.farmers?.length === 0) {
      notify(t('Please select farmers'), 'error')
      return
    }
    setIsBusy(true)
    try {
      if (id) {
        await IndividualFiltersService.filtersGeneralUpdate(id, data)
        notify(t('Successfully updated general filter'), 'success')
      } else {
        await IndividualFiltersService.filtersGeneralStore(data)
        notify(t('Successfully created general filter'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS_GENERAL)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function renderFarmers() {
    const displayedFarmers = fields.slice(0, MIN_FARMERS_TO_DISPLAY)
    return displayedFarmers.map((farmer, index) => {
      return <div key={farmer.key} style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <SelectedContent key={farmer.key} as={Link} to={RouteTypes.DAIRY_FACTORY_FARMERS_EDIT.replace(':id', farmer['id'])}>
          <div style={{ display: 'flex' }}>
            <div
              className='title'
            >{`${index + 1}. ${farmer['companyName']}`}
            </div>
          </div>
        </SelectedContent>
        <Icon
          name='trash'
          onClick={() => remove(index)}
          color='red'
          style={{ cursor: 'pointer' }}
        />
      </div>
    })
  }

  function renderForm() {
    return <>
      <FormFields.Input
        control={control}
        name={'name'}
        placeholder={t('Enter name')}
        rules={{
          required: t('Please enter name!'),
          maxLength: {
            value: 150,
            message: t('Max char. length is 150!')
          }
        }}
        label={t('Name')}
      />
      <SelectedWrapper>
        {renderFarmers()}
        {fields.length > MIN_FARMERS_TO_DISPLAY && <Popup
          content={t('Show more')}
          trigger={<Icon
            onClick={() => setIsDialogOpen(true)}
            name='ellipsis horizontal'
            size='large'
            style={{
              cursor: 'pointer',
              fontSize: '1.1em',
              boxShadow: '0 0 0 .1em rgba(0,0,0,0.3) inset'
            }}
            circular
          />}
        />}
      </SelectedWrapper>
      <div style={{ minHeight: '17em' }}>
        <InfitiveScrollDropown
          control={control}
          subFarmers
          scrolling={true}
          selectOnBlur={false}
          name={''}
          label={t('Farmers')}
          maxHeightMenu={'11em'}
          loading={isLoadingFarmers}
          values={fields}
          disabled={isLoadingFarmers}
          value={''}
          onChange={(e, farmer) => {
            prepend({
              ...farmer,
              id: farmer.value,
              companyName: farmer.text
            })
          }}
          closeOnChange={false}
          setValue={setValue}
          placeholder={t('Select farmer')}
          options={farmers?.data?.map(farmer => ({ value: farmer.id, text: farmer.companyName })) || []}
        />
      </div>
    </>
  }

  function renderActions() {
    return <>
      <El.PageHeaderActionButton type='submit'
        disabled={isBusy}
        loading={isBusy}>{t('Save')}</El.PageHeaderActionButton>
      <El.PageHeaderActionButton
        type='button'
        onClick={() => navigate(RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS_GENERAL)}
      >{t('Cancel')}</El.PageHeaderActionButton>
    </>
  }

  function renderShowMoreDialog() {
    return <ShowMoreDialog
      isDialogOpen={isDialogOpen}
      handleClose={() => setIsDialogOpen(false)}
      remove={remove}
      farmers={fields}
    />
  }

  return <Layout
    title={id ? t('Edit general filter') : t('Create general filter')}
    onSubmit={handleSubmit(onSubmit)}
    fontSizeHeader={'18px'}
    childContainerSize={'300px'}
    isBusy={isBusy}
    actions={renderActions()}
  >
    {renderShowMoreDialog()}
    {renderForm()}
  </Layout>
}
export default CreateEditGenFilter